import { z } from 'zod';
import { authoredMatchRegionSchema, authoringTimeSchema, interactionTagIdSchema, macroWorkspaceSchema,
  recordingPinSchema } from './authoringPrimitives';
import { macroInputModePolicyOverrideSchema, macroInputModePolicySchema } from './inputModePolicy';
import { inputEditSchema } from './sourceGraph';

export const authoredInputGroupSchema = z.object({ groupId: z.string().uuid(), name: z.string().min(1).max(80),
  firstSeq: authoringTimeSchema, lastSeq: authoringTimeSchema, fromUs: authoringTimeSchema, toUs: authoringTimeSchema,
  gateTagId: interactionTagIdSchema.nullable(), regions: z.array(authoredMatchRegionSchema).max(64),
  policyOverride: macroInputModePolicyOverrideSchema.optional(),
}).strict().superRefine((group, context) => {
  if (group.firstSeq > group.lastSeq || group.fromUs >= group.toUs) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Input group needs an ordered nonempty range' });
  }
  if ((group.gateTagId === null) !== (group.regions.length === 0)
    || group.regions.some(region => region.tagId !== group.gateTagId)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Only authored regions create a gate; every region must share its gate tag' });
  }
  const gateSequence = group.gateTagId === null ? null : Number(group.gateTagId.split(':')[1]);
  if (gateSequence !== null && (gateSequence < group.firstSeq || gateSequence > group.lastSeq)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Gate tag must belong to the input group' });
  }
});
export type AuthoredInputGroup = z.infer<typeof authoredInputGroupSchema>;
export const macroAuthoringDocumentSchema = z.object({ format: z.literal('playcast.macro.authoring'), formatVersion: z.literal(1),
  macroId: z.string().uuid(), workspace: macroWorkspaceSchema, revision: authoringTimeSchema, name: z.string().min(1).max(80),
  recording: recordingPinSchema, range: z.object({ fromUs: authoringTimeSchema, toUs: authoringTimeSchema }).strict(),
  policy: macroInputModePolicySchema, edits: z.array(inputEditSchema).max(10_000),
  groups: z.array(authoredInputGroupSchema).max(4096),
}).strict().superRefine((document, context) => {
  const fail = (message: string): void => context.addIssue({ code: z.ZodIssueCode.custom, message });
  if (document.range.fromUs >= document.range.toUs) fail('Authoring range must be nonempty');
  if (document.edits.some(edit => edit.op !== 'insert' && edit.packageId !== document.recording.packageId)) {
    fail('Input edits must target the pinned recording');
  }
  const groups = document.groups;
  const regionIds = groups.flatMap(group => group.regions.map(region => region.regionId));
  if (new Set(groups.map(group => group.groupId)).size !== groups.length || new Set(regionIds).size !== regionIds.length) {
    fail('Authored group and region identities must be unique');
  }
  for (const [index, group] of groups.entries()) {
    const previous = groups[index - 1];
    if (group.fromUs !== (previous?.toUs ?? document.range.fromUs)
      || (previous && previous.lastSeq >= group.firstSeq)
      || group.regions.some(region => region.packageId !== document.recording.packageId)) fail('Groups must partition one recording without overlap');
  }
  if (groups.length > 0 && groups[groups.length - 1]!.toUs !== document.range.toUs) fail('Group ranges must cover the final exclusive bound');
});
export type MacroAuthoringDocument = z.infer<typeof macroAuthoringDocumentSchema>;

/** Compiler namespace only; never use this string as proof of campaign ownership. */
export function macroAuthoringScope(macroId: string): string {
  return `macro:${z.string().uuid().parse(macroId)}`;
}
export function macroFlowScope(workspace: z.infer<typeof macroWorkspaceSchema>): string {
  const valid = macroWorkspaceSchema.parse(workspace);
  return valid.kind === 'campaign' ? valid.campaignId : `local:${valid.workspaceId}`;
}
